import { computed, observable, runInAction } from 'mobx'
import { catchError, distinctUntilChanged, EMPTY, switchMap } from 'rxjs'
import type {
  FetchClientAssignmentFn,
  FetchMemberFn,
  FetchMembersByPublicIdsFn,
  GetCurrentMemberFn,
} from '@/features/iam'
import { type Member } from '@/features/iam/members/Member'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { GetUserPublicIdFn } from '@/features/client-user-management'
import type { ActivationLifecycle } from '@/infra/activation'
import { type Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction, toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'

/**
 * A user's client assignment in the current workspace: their advisors when
 * they are a client, or whether they are an advisor with clients of their own.
 */
export class ClientAssignmentViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The user as a member of the current workspace, or `null` until it loads.
   */
  @observable
  accessor member: Member | null = null

  /**
   * The user's active advisors, or `null` until they load or when the current
   * member may not see them.
   */
  @observable
  accessor advisors: Member[] | null = null

  constructor(
    private readonly _getUserPublicId: GetUserPublicIdFn,
    private readonly _canHaveAssignment: () => boolean,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _fetchMember: FetchMemberFn,
    private readonly _fetchMembersByPublicIds: FetchMembersByPublicIdsFn,
    private readonly _fetchClientAssignment: FetchClientAssignmentFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    // The assignment belongs to the workspace, so it loads again when it
    // changes.
    this._lifecycle.manage(
      toStream(
        () =>
          this._canViewAssignments && this._canHaveAssignment()
            ? this._getCurrentMember().workspace.id
            : null,
        true,
      ).pipe(
        distinctUntilChanged(),
        tapAction(() => {
          this.member = null
          this.advisors = null
        }),
        switchMap((workspaceId) =>
          workspaceId
            ? withAbortSignal((signal) =>
                this._createAlertRetryWrapper(
                  'Loading advisors failed',
                  signal,
                )(() => this._fetchAssignment(workspaceId, signal)),
              ).pipe(
                // The retry alert has reported the failure.
                catchError(() => EMPTY),
              )
            : EMPTY,
        ),
      ),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Whether the user is an advisor, who is assigned clients.
   */
  @computed
  get isAdvisor() {
    return this.member?.permissions.clientAssignmentManage ?? false
  }

  /**
   * Whether the advisor's clients are shown.
   */
  @computed
  get shouldShowClientsTab() {
    return (
      this.isAdvisor &&
      this._getCurrentMember().permissions.workspaceClientAssignmentManage
    )
  }

  /**
   * Whether the client's advisors are shown, once they have loaded.
   */
  @computed
  get shouldShowAdvisors() {
    return this.advisors !== null
  }

  /**
   * Whether the current member may open an advisor's details.
   */
  @computed
  get canOpenAdvisors() {
    return this._getCurrentMember().permissions.workspaceClientAssignmentManage
  }

  /**
   * Whether the current member may see client assignments in this workspace.
   */
  @computed
  private get _canViewAssignments() {
    const member = this._getCurrentMember()
    return (
      member.workspace.config.externalCustomer.clientAssignmentEnabled &&
      (member.permissions.workspaceClientAssignmentManage ||
        member.permissions.clientAssignmentManage)
    )
  }

  /**
   * Loads the user's membership, then their advisors when they are a client.
   */
  private async _fetchAssignment(workspaceId: Id, signal: AbortSignal) {
    const member = await this._fetchMember(
      { workspaceId, userPublicId: this._getUserPublicId() },
      signal,
    )
    runInAction(() => (this.member = member))
    if (!member || this.isAdvisor) {
      return
    }

    const assignment = await this._fetchClientAssignment(
      workspaceId,
      member.user.publicId,
      signal,
    )
    if (!assignment) {
      return
    }

    const advisors = await this._fetchMembersByPublicIds(
      assignment.advisorUserPublicIds.map((userPublicId) => ({
        workspaceId,
        userPublicId,
      })),
      signal,
    )
    runInAction(() => {
      this.advisors = advisors.filter(
        (advisor): advisor is Member => !!advisor && !advisor.inactive,
      )
    })
  }
}
